import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { cn } from "@/shared/lib/cn";
import { ProductBlockHeading } from "./ProductBlockHeading";

export type ProductStatSection = NonNullable<EditorialContent["stats"]>;

export interface ProductStatsProps {
  section: ProductStatSection;
  number?: string;
}

/**
 * Las cifras grandes.
 *
 * ── El número de columnas depende de cuántas cifras hay ──────────────
 * Tres cifras en una rejilla de cuatro dejan un hueco al final que se lee
 * como un dato que falta. Cuatro o más van de cuatro en cuatro; tres o
 * menos, de tres. En el teléfono siempre son dos: 56 px de número en
 * media pantalla ya es todo lo grande que puede ser sin partirse.
 *
 * ── El número NO es un `<h3>` ────────────────────────────────────────
 * "1,100 mg" no es el título de nada: el título es la etiqueta de abajo
 * ("Mezcla patentada"). Un índice de encabezados lleno de cifras sueltas
 * no sirve para navegar. Por eso es un `<dl>`: la etiqueta es el término
 * y el número su definición — aunque en pantalla el número vaya primero,
 * que es lo que el ojo quiere y `flex-col-reverse` resuelve sin tocar el
 * orden del documento.
 *
 * ── El rótulo va en `ds-ink-3`, no en `ds-ink-4` ─────────────────────
 * La ficha original lo pinta en #8A8377: 3.45:1 sobre blanco, por debajo
 * del mínimo para un texto de 10 px. `ds-ink-3` da 12.2:1.
 */
export function ProductStats({ section, number }: ProductStatsProps) {
  const { t } = useTranslation();

  const wide = section.items.length >= 4;

  return (
    <section className="grid gap-7">
      <ProductBlockHeading
        eyebrow={t("En cifras")}
        number={number}
        title={section.title}
        intro={section.intro}
      />

      <dl
        className={cn(
          "grid grid-cols-2 gap-3",
          wide ? "lg:grid-cols-4" : "lg:grid-cols-3",
        )}
      >
        {section.items.map((stat) => (
          <div
            key={`${stat.number}-${stat.label}`}
            className="flex flex-col-reverse rounded-[28px] border border-ds-lavender-bg-2 bg-gradient-to-b from-ds-lavender-bg to-white px-5 py-8 text-center shadow-ds-xs"
          >
            <dt className="grid gap-1.5">
              <span className="font-mono text-[10px] uppercase leading-[1.4] tracking-[0.14em] text-ds-ink-3">
                {stat.label}
              </span>

              {stat.description && (
                <span className="text-[12px] leading-[1.45] text-ds-ink-3">
                  {stat.description}
                </span>
              )}
            </dt>

            {/* El mínimo del `clamp` es 28 px y no 38: "1,100 mg" a 38 px no
                cabe en media pantalla de 390 px y parte la cifra en dos
                renglones, que descuadra la tarjeta contra la de al lado. */}
            <dd className="mb-2 text-[clamp(28px,5vw,56px)] font-light leading-none tracking-[-0.03em] text-ds-violet-pop">
              {stat.number}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
